import { Modal, RadioOption, TextInput } from '@/components/common/Modal'
import { useAutosave } from '@/components/common/useAutosave'
import { FieldMarker, FieldRequiredRail } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import { StatusPill } from '@/components/order/FormCard'
import type { CanonicalGuardianContact } from '@/ordering/canonicalOrder'
import {
  GUARDIAN_RELATIONSHIP_OPTIONS,
  LEGACY_GUARDIAN_RELATIONSHIP_OPTIONS,
  formatGuardianRelationshipLabel,
  isGuardianRelationshipValue,
} from '@/ordering/canonicalOrder'
import {
  isValidOrderContactEmail,
  isValidOrderContactPhone,
  orderContactEmailError,
  orderContactPhoneError,
} from '@/utils/contactValidation'
import {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NAME_TOO_LONG_MESSAGE,
  PHONE_MAX_LENGTH,
  isValidName,
  isWithinLength,
  nameError,
} from '@/utils/freeTextValidation'
import { ArrowRight, ChevronDown, Envelope, Phone, QuestionCircle } from 'flowbite-react-icons/outline'
import { useEffect, useMemo, useRef, useState } from 'react'

export type GuardianContactMode = 'unavailable' | 'enterInfo'

export interface GuardianContactModalState {
  mode: GuardianContactMode
  firstName: string
  lastName: string
  /** ISO YYYY-MM-DD. Retained for round-trip with existing orders; not edited in R5. */
  dateOfBirth: string
  email: string
  phone: string
  relationshipToPatient: string
}

const SOURCE_SEGMENTS: { id: GuardianContactMode; label: string }[] = [
  { id: 'unavailable', label: 'Contact Not Available' },
  { id: 'enterInfo', label: 'Enter Contact Info' },
]

function emptyFieldsState(): Pick<
  GuardianContactModalState,
  'firstName' | 'lastName' | 'dateOfBirth' | 'email' | 'phone' | 'relationshipToPatient'
> {
  return {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    relationshipToPatient: '',
  }
}

function canonicalToState(g: CanonicalGuardianContact | null | undefined): GuardianContactModalState {
  if (g?.contactNotAvailable === true) {
    return { mode: 'unavailable', ...emptyFieldsState() }
  }
  const relRaw = g?.relationshipToPatient?.trim() ?? ''
  const relationshipToPatient = isGuardianRelationshipValue(relRaw) ? relRaw : ''
  // Default to "Enter Contact Info" whenever we are not explicitly "unavailable".
  // Autosave persists { contactNotAvailable: false, fields: null } for an empty form; if we
  // mapped that to "unavailable" (no hasFields), the initialCanonical effect would snap the UI
  // back and hide the form right after selecting Enter Contact Info.
  return {
    mode: 'enterInfo',
    firstName: g?.firstName?.trim() ?? '',
    lastName: g?.lastName?.trim() ?? '',
    dateOfBirth: g?.dateOfBirth?.trim() ?? '',
    email: g?.email?.trim() ?? '',
    phone: g?.phone?.trim() ?? '',
    relationshipToPatient,
  }
}

function stateToCanonical(data: GuardianContactModalState): CanonicalGuardianContact | null {
  if (data.mode === 'unavailable') {
    return {
      contactNotAvailable: true,
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      email: null,
      phone: null,
      relationshipToPatient: null,
    }
  }
  const firstName = data.firstName?.trim() || undefined
  const lastName = data.lastName?.trim() || undefined
  const dateOfBirth = data.dateOfBirth?.trim() || undefined
  const email = data.email?.trim() || undefined
  const phone = data.phone?.trim() || undefined
  const rel = data.relationshipToPatient?.trim()
  const relationshipToPatient = rel && isGuardianRelationshipValue(rel) ? rel : undefined
  if (!firstName && !lastName && !dateOfBirth && !email && !phone && !relationshipToPatient) {
    return {
      contactNotAvailable: false,
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      email: null,
      phone: null,
      relationshipToPatient: null,
    }
  }
  return {
    contactNotAvailable: false,
    firstName: firstName ?? null,
    lastName: lastName ?? null,
    dateOfBirth: dateOfBirth ?? null,
    email: email ?? null,
    phone: phone ?? null,
    relationshipToPatient: relationshipToPatient ?? null,
  }
}

interface GuardianContactModalProps {
  isOpen: boolean
  onClose: () => void
  initialCanonical: CanonicalGuardianContact | null | undefined
  onSave: (canonical: CanonicalGuardianContact | null) => void
  /**
   * When provided, the footer renders a brand "Continue to …" CTA (label from
   * `continueLabel`) that persists the contact and advances to the next order
   * section modal, demoting "Done" to the secondary style. Subject to the same
   * required-field gate as Done.
   */
  onContinue?: () => void
  continueLabel?: string
  /**
   * When true, the Relationship to Patient picker offers the full extended IMDB
   * lineage vocabulary (newer side-nav / comparator-decoupled flow). When false
   * (default), it is restricted to the four legacy consent values
   * (MOTHER/FATHER/LEGAL_GUARDIAN/OTHER) that the legacy mother/father-only flow
   * has always offered.
   */
  allowExtendedRelationships?: boolean
}

export function GuardianContactModal({
  isOpen,
  onClose,
  initialCanonical,
  onSave,
  onContinue,
  continueLabel = 'Continue',
  allowExtendedRelationships = false,
}: GuardianContactModalProps) {
  const [data, setData] = useState<GuardianContactModalState>(() => canonicalToState(initialCanonical))
  const [blockedCloseAttempt, setBlockedCloseAttempt] = useState(false)
  const baseRelationshipOptions = allowExtendedRelationships
    ? GUARDIAN_RELATIONSHIP_OPTIONS
    : LEGACY_GUARDIAN_RELATIONSHIP_OPTIONS
  // The legacy picker only lists the four core values, but a stored relationship
  // may be an extended lineage value carried over from the newer flow (e.g.
  // SIBLING, GRANDMOTHER_MATERNAL). Append the current value as an extra option
  // so the control shows the real selection instead of silently rendering blank
  // — which would let a user unknowingly overwrite it with a legacy choice.
  const relationshipOptions = useMemo(() => {
    const current = data.relationshipToPatient?.trim()
    if (!current || baseRelationshipOptions.some((o) => o.value === current)) {
      return baseRelationshipOptions
    }
    if (!isGuardianRelationshipValue(current)) return baseRelationshipOptions
    return [
      ...baseRelationshipOptions,
      { value: current, label: formatGuardianRelationshipLabel(current) ?? current },
    ]
  }, [baseRelationshipOptions, data.relationshipToPatient])
  const { isSaving } = useAutosave(data, (d) => onSave(stateToCanonical(d)), 500, isOpen)

  // Snapshot pre-edit canonical when the modal opens so Discard reverts to that
  // baseline rather than the latest autosaved state.
  const initialCanonicalRef = useRef(initialCanonical)
  initialCanonicalRef.current = initialCanonical
  const openBaselineRef = useRef<CanonicalGuardianContact | null | undefined>(initialCanonical)

  // R5: at least one valid contact method (phone OR email) is required, and any
  // provided method must be a valid format.
  const canCloseGuardianModal = useMemo(() => {
    if (data.mode === 'unavailable') return true
    const fn = data.firstName?.trim()
    const ln = data.lastName?.trim()
    const rel = data.relationshipToPatient?.trim()
    if (!fn || !ln || !rel || !isGuardianRelationshipValue(rel)) return false
    if (!isValidName(data.firstName) || !isValidName(data.lastName)) return false
    if (!isWithinLength(data.firstName, NAME_MAX_LENGTH) || !isWithinLength(data.lastName, NAME_MAX_LENGTH)) {
      return false
    }
    const emailProvided = Boolean(data.email?.trim())
    const phoneProvided = Boolean(data.phone?.trim())
    const emailOk = !emailProvided || isValidOrderContactEmail(data.email)
    const phoneOk = !phoneProvided || isValidOrderContactPhone(data.phone)
    const atLeastOne = isValidOrderContactEmail(data.email) || isValidOrderContactPhone(data.phone)
    return emailOk && phoneOk && atLeastOne
  }, [data])

  useEffect(() => {
    if (isOpen) setBlockedCloseAttempt(false)
  }, [isOpen])

  // Hydrate from Redux only when the modal opens. While open, autosave updates
  // `initialCanonical` with the same field values, so re-running canonicalToState
  // would fight in-progress edits.
  const wasModalOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen) {
      if (!wasModalOpenRef.current) {
        openBaselineRef.current = initialCanonicalRef.current
        setData(canonicalToState(initialCanonicalRef.current))
      }
      wasModalOpenRef.current = true
    } else {
      wasModalOpenRef.current = false
    }
  }, [isOpen])

  const handleDiscard = () => {
    // Read the open-time snapshot — NOT the live `initialCanonical` prop. Autosave fires every
    // 500ms and pushes intermediate edits back through Redux, which mutates `initialCanonical`.
    // Comparing/dispatching against the live prop would (a) make `dataChanged` false right after
    // an autosave tick — silently keeping the user's edits — and (b) revert to the autosaved
    // value rather than the original baseline.
    const baseline = openBaselineRef.current
    const reverted = canonicalToState(baseline)
    const dataChanged = JSON.stringify(data) !== JSON.stringify(reverted)
    if (dataChanged) {
      setData(reverted)
      // Pass the open-time baseline directly (not `stateToCanonical(reverted)`) to preserve
      // null-vs-empty-object exactness so a discarded edit does not mark the guardian dirty.
      onSave(baseline ?? null)
    }
    onClose()
  }

  const handleDone = () => {
    onSave(stateToCanonical(data))
    onClose()
  }

  const handleRequestClose = () => {
    if (!canCloseGuardianModal) {
      setBlockedCloseAttempt(true)
      return
    }
    setBlockedCloseAttempt(false)
    handleDone()
  }

  const handleContinue = () => {
    if (!canCloseGuardianModal) {
      setBlockedCloseAttempt(true)
      return
    }
    setBlockedCloseAttempt(false)
    onSave(stateToCanonical(data))
    onContinue?.()
    onClose()
  }

  const setMode = (mode: GuardianContactMode) => {
    setData((prev) => {
      if (mode === 'unavailable') {
        return { mode: 'unavailable', ...emptyFieldsState() }
      }
      return { ...prev, mode: 'enterInfo' }
    })
  }

  const updateField = (field: keyof Omit<GuardianContactModalState, 'mode'>, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  const showEditableForm = data.mode === 'enterInfo'

  /**
   * Reveal required-field errors only on an advance/close attempt
   * (`blockedCloseAttempt`) — NOT merely because the user has started typing in
   * some other field. Per-field required errors still self-reveal on that
   * field's own blur (the shared `TextInput` gates on its internal `touched`
   * state), and disallowed-character/format errors surface immediately
   * regardless (handled via the `*CharErr` paths below). This prevents typing in
   * one field (e.g. last name) from immediately erroring the still-untouched
   * empty required siblings (UX-022 c).
   */
  const showEnterInfoErrors = Boolean(showEditableForm && !canCloseGuardianModal && blockedCloseAttempt)

  // Name-pattern/length validity is a hard constraint (mirrors the server name field validator)
  // and supersedes the required-field message so the disallowed-character feedback shows first.
  const firstNameCharErr =
    nameError(data.firstName) ?? (!isWithinLength(data.firstName, NAME_MAX_LENGTH) ? NAME_TOO_LONG_MESSAGE : undefined)
  const lastNameCharErr =
    nameError(data.lastName) ?? (!isWithinLength(data.lastName, NAME_MAX_LENGTH) ? NAME_TOO_LONG_MESSAGE : undefined)
  const firstNameErr = firstNameCharErr ?? (!data.firstName?.trim() ? 'Enter a first name' : undefined)
  const lastNameErr = lastNameCharErr ?? (!data.lastName?.trim() ? 'Enter a last name' : undefined)
  const relTrim = data.relationshipToPatient?.trim()
  const relationshipErr = !relTrim
    ? 'Select a relationship'
    : !isGuardianRelationshipValue(relTrim)
      ? 'Select a valid relationship'
      : undefined

  const emailProvided = Boolean(data.email?.trim())
  const phoneProvided = Boolean(data.phone?.trim())
  const contactMissing = !emailProvided && !phoneProvided
  // Phone and email now have separate headers; the "at least one required"
  // rule is a group-level constraint surfaced in a single message beneath the
  // two fields. Per-field errors stay scoped to format validation.
  const phoneErrText = phoneProvided ? orderContactPhoneError(data.phone) : undefined
  const emailErrText = emailProvided ? orderContactEmailError(data.email) : undefined

  const showFirstNameError = Boolean(firstNameCharErr) || Boolean(showEnterInfoErrors && firstNameErr)
  const showLastNameError = Boolean(lastNameCharErr) || Boolean(showEnterInfoErrors && lastNameErr)
  const showRelationshipError = Boolean(showEnterInfoErrors && relationshipErr)
  const showPhoneError = Boolean(showEnterInfoErrors && phoneErrText)
  const showEmailError = Boolean(showEnterInfoErrors && emailErrText)
  // Group-level "at least one required" state: neither provided AND errors
  // should surface. Marks both fields invalid (red) with a shared message.
  const showContactRequiredError = Boolean(showEnterInfoErrors && contactMissing)

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleRequestClose}
      title="Guardian Contact"
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-4">
          <button
            type="button"
            onClick={handleDiscard}
            className="rounded-full border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium leading-5 text-text-body shadow-xs transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Discard Changes
          </button>
          {!onContinue && (
            <button
              type="button"
              onClick={handleRequestClose}
              className="rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSaving}
            >
              Done
            </button>
          )}
          {onContinue && (
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSaving}
            >
              {continueLabel}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-6 sm:gap-8">
        <div className="flex gap-2 rounded-xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-900/40 dark:bg-brand-950/20">
          <QuestionCircle className="mt-0.5 h-4 w-4 shrink-0 text-text-fg-info" aria-hidden />
          <p className="text-sm leading-5 text-text-fg-info">
            Guardian contact will be used for communication for the proband and for research contact.
          </p>
        </div>

        {blockedCloseAttempt && !canCloseGuardianModal && showEditableForm ? (
          <StatusPill
            text="Complete all required fields with a valid phone number and/or email before continuing."
            variant="info"
          />
        ) : null}

        <div className="flex w-full flex-row" role="radiogroup" aria-label="Guardian contact source">
          {SOURCE_SEGMENTS.map((seg, index) => (
            <RadioOption
              key={seg.id}
              name="guardian-contact-mode"
              value={seg.id}
              checked={data.mode === seg.id}
              onChange={(value) => setMode(value as GuardianContactMode)}
              label={seg.label}
              first={index === 0}
              last={index === SOURCE_SEGMENTS.length - 1}
              layout="inline"
            />
          ))}
        </div>

        {showEditableForm ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <TextInput
                label="Guardian's First Name"
                value={data.firstName}
                onChange={(v) => updateField('firstName', v)}
                required
                variant="soft"
                maxLength={NAME_MAX_LENGTH}
                error={firstNameErr}
                showError={showFirstNameError}
              />
              <TextInput
                label="Guardian's Last Name"
                value={data.lastName}
                onChange={(v) => updateField('lastName', v)}
                required
                variant="soft"
                maxLength={NAME_MAX_LENGTH}
                error={lastNameErr}
                showError={showLastNameError}
              />
              <div className="flex items-stretch gap-2">
                <FieldRequiredRail
                  state={resolveFieldMarkerState(true, Boolean(relTrim))}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                <label
                  className={`mb-1.5 flex items-center text-sm font-medium ${showRelationshipError ? 'text-error-600 dark:text-error-400' : 'text-text-heading'}`}
                  htmlFor="guardian-relationship"
                >
                  Relationship to Patient
                  <FieldMarker
                    state={resolveFieldMarkerState(true, Boolean(relTrim))}
                    fieldLabel="Relationship to Patient"
                  />
                </label>
                <div className="relative">
                  <select
                    id="guardian-relationship"
                    value={data.relationshipToPatient}
                    onChange={(e) => updateField('relationshipToPatient', e.target.value)}
                    aria-required="true"
                    aria-invalid={showRelationshipError}
                    aria-describedby={showRelationshipError ? 'guardian-relationship-error' : undefined}
                    className={`w-full appearance-none rounded-xl border py-2.5 pl-4 pr-10 text-sm shadow-xs focus:outline-none focus:ring-2 dark:text-white ${
                      showRelationshipError
                        ? 'border-error-500 bg-error-50 text-error-800 focus:border-error-600 focus:ring-error-500/25 dark:border-error-500 dark:bg-gray-900 dark:text-error-100'
                        : 'border-border-base bg-background-gray text-text-heading focus:border-brand-600 focus:ring-brand-600/20 dark:border-gray-600 dark:bg-gray-800/80'
                    }`}
                  >
                    <option value="">Select relationship</option>
                    {relationshipOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${showRelationshipError ? 'text-error-600 dark:text-error-400' : 'text-text-body'}`}
                    aria-hidden
                  />
                </div>
                {showRelationshipError && relationshipErr ? (
                  <p
                    id="guardian-relationship-error"
                    className="mt-2.5 text-sm text-error-600 dark:text-error-400"
                    role="alert"
                  >
                    {relationshipErr}
                  </p>
                ) : null}
                </div>
              </div>
            </div>
            {/* R5: phone and email have separate headers; at least one is
                required. The requirement is surfaced as a single message
                beneath both fields (per-field errors handle format issues). */}
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:items-end">
                <TextInput
                  label="Guardian's Phone Number"
                  type="tel"
                  value={data.phone}
                  onChange={(v) => updateField('phone', v)}
                  placeholder="(555) 555-5555"
                  icon={<Phone className="h-4 w-4" aria-hidden />}
                  variant="soft"
                  maxLength={PHONE_MAX_LENGTH}
                  required
                  ariaRequired={false}
                  markerFilled={Boolean(data.email?.trim() || data.phone?.trim())}
                  error={phoneErrText}
                  showError={showPhoneError || showContactRequiredError}
                  invalid={showContactRequiredError}
                />
                <TextInput
                  label="Guardian's Email"
                  type="email"
                  value={data.email}
                  onChange={(v) => updateField('email', v)}
                  placeholder="Enter email"
                  icon={<Envelope className="h-4 w-4" aria-hidden />}
                  variant="soft"
                  maxLength={EMAIL_MAX_LENGTH}
                  required
                  ariaRequired={false}
                  markerFilled={Boolean(data.email?.trim() || data.phone?.trim())}
                  error={emailErrText}
                  showError={showEmailError || showContactRequiredError}
                  invalid={showContactRequiredError}
                />
              </div>
              <p
                className={`text-xs leading-5 ${
                  showContactRequiredError
                    ? 'text-error-600 dark:text-error-400'
                    : 'text-text-body-subtle dark:text-gray-500'
                }`}
                role={showContactRequiredError ? 'alert' : undefined}
                data-testid="guardian-contact-channel-message"
              >
                Enter an email or phone number
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
